import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RocketOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { useResponsive } from '../../hooks/useResponsive';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { notificationController } from '../../controllers/notificationController';

import { PageContainer, ContentCard } from '../../components/layouts/PageLayouts';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { CreatingButton } from '../../components/common/buttons/CreatingButton';
import { PublishAssessmentModal } from '../../components/common/modals/PublishAssessmentModal';
import { URLs } from '../../store/urls';

// Types
interface AnswerSheet {
  id: number;
  name: string;
  format: string;
  questions: number;
  createdAt: string;
}

type AnswerSheetStatus = 'idle' | 'creating' | 'polling' | 'ready' | 'error';

// Styled Components
const AssessmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AssessmentTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .icon {
    font-size: 28px;
  }
  
  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-left: 40px;
  
  @media (max-width: 768px) {
    margin-left: 0;
    gap: 12px;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6B7280;
  
  .stat-label {
    font-weight: 500;
  }
  
  .stat-value {
    color: #1a1a1a;
    font-weight: 600;
  }
  
  .stat-icon {
    font-size: 14px;
  }
`;

const StatusBadge = styled.span<{ status: 'draft' | 'scheduled' | 'active' | 'completed' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  
  ${props => {
    switch (props.status) {
      case 'draft':
        return `
          background: rgba(107, 114, 128, 0.1);
          color: #4B5563;
        `;
      case 'scheduled':
        return `
          background: rgba(59, 130, 246, 0.1);
          color: #2563EB;
        `;
      case 'active':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16A34A;
        `;
      case 'completed':
        return `
          background: rgba(139, 92, 246, 0.1);
          color: #7C3AED;
        `;
      default:
        return `
          background: rgba(107, 114, 128, 0.1);
          color: #4B5563;
        `;
    }
  }}
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    ${props => {
      switch (props.status) {
        case 'draft':
          return `background: #6B7280;`;
        case 'scheduled':
          return `background: #3B82F6;`;
        case 'active':
          return `background: #22C55E;`;
        case 'completed':
          return `background: #8B5CF6;`;
        default:
          return `background: #6B7280;`;
      }
    }}
  }
`;

const AssessmentActions = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    
    button {
      flex: 1;
    }
  }
`;

const PublishButton = styled(BusinessButton)`
  min-width: 140px;
`;

// Answer Sheets Info Section
const AnswerSheetsInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 40px;
  text-align: center;
  min-height: 280px;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    min-height: 240px;
  }
`;

const InfoIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 52px;
    margin-bottom: 16px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const InfoDescription = styled.p`
  font-size: 15px;
  color: #6B7280;
  margin: 0 0 24px 0;
  max-width: 500px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const SuccessBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.15) 100%);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
  
  .badge-icon {
    font-size: 24px;
  }
  
  .badge-text {
    font-size: 16px;
    font-weight: 600;
    color: #16A34A;
    
    .count {
      font-size: 20px;
      font-weight: 700;
      color: #15803D;
    }
  }
`;

const WarningBadge = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 20px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 10px;
  margin-bottom: 20px;
  max-width: 600px;
  
  .warning-icon {
    font-size: 20px;
    line-height: 1;
    margin-top: 2px;
  }
  
  .warning-text {
    font-size: 14px;
    font-weight: 500;
    color: #d97706;
    line-height: 1.6;
    
    .highlight {
      font-weight: 700;
      color: #b45309;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
`;

const DownloadButton = styled(BusinessButton)`
  min-width: 180px;
`;

const CreateButton = styled(BusinessButton)`
  min-width: 180px;
`;

const AssessmentPage: React.FC = () => {
  const { assessmentName } = useParams<{ assessmentName: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  
  // Get assessment ID from URL params
  const assessmentId = searchParams.get('id');
  
  // Redux state & actions
  const { assessments } = useTypedSelector<any>(state => state.assessments);
  const { classes } = useTypedSelector<any>(state => state.classes);
  const { getAssessmentsRequest, getClassesRequest, updateAssessmentRequest } = useActions();
  
  // Local state
  const [assessment, setAssessment] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  
  // Answer sheets state
  const [answerSheets, setAnswerSheets] = useState<AnswerSheet[]>([]);
  const [answerSheetStatus, setAnswerSheetStatus] = useState<AnswerSheetStatus>('idle');
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Polling ref
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Decode assessment name for display
  const decodedAssessmentName = assessmentName 
    ? decodeURIComponent(assessmentName).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    : 'Assessment';
  
  // Load assessment data
  useEffect(() => {
    // Add userId to URL if authenticated
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null' && !searchParams.get('userId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('userId', storedUserId);
      setSearchParams(newParams, { replace: true });
    }
    
    if (assessmentId) {
      // Store for future reference
      localStorage.setItem('currentAssessmentId', assessmentId);
      
      // Load assessment data and classes
      getAssessmentsRequest();
      getClassesRequest();
    } else {
      // Try to get from localStorage if no ID in params
      const storedAssessmentId = localStorage.getItem('currentAssessmentId');
      if (storedAssessmentId) {
        // Reload if we have stored ID
        getAssessmentsRequest();
        getClassesRequest();
      }
    }
  }, [assessmentId]);
  
  // Find current assessment from loaded assessments
  useEffect(() => {
    if (assessments && assessmentId) {
      const currentAssessment = assessments.find((a: any) => a.id === Number(assessmentId));
      if (currentAssessment) {
        setAssessment(currentAssessment);
      }
    }
  }, [assessments, assessmentId]);
  
  // Load answer sheets for assessment
  useEffect(() => {
    if (assessmentId) {
      fetchAnswerSheets();
    }
  }, [assessmentId]);
  
  // Polling mechanism for answer sheets
  useEffect(() => {
    if (answerSheetStatus === 'polling') {
      console.log('[ASSESSMENT PAGE] Starting polling for answer sheets...');
      pollingIntervalRef.current = setInterval(() => {
        console.log('[ASSESSMENT PAGE] Polling answer sheets...');
        fetchAnswerSheets();
      }, 5000); // Poll every 5 seconds
    } else {
      // Clear polling when status changes
      if (pollingIntervalRef.current) {
        console.log('[ASSESSMENT PAGE] Stopping polling');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [answerSheetStatus]);
  
  // Update status when answer sheets arrive
  useEffect(() => {
    if (answerSheets.length > 0 && answerSheetStatus !== 'ready') {
      console.log('[ASSESSMENT PAGE] Answer sheets received, setting status to ready');
      const wasGenerating = answerSheetStatus === 'polling' || answerSheetStatus === 'creating';
      
      // Calculate expected students count
      let expectedStudents = assessment?.studentsCount || assessment?.countOfStudents || 0;
      if (expectedStudents === 0 && classes && assessment) {
        const assessmentClassIds = assessment.classIds || (assessment.classId ? [assessment.classId] : []);
        assessmentClassIds.forEach((classId: number) => {
          const classData = classes.find((c: any) => c.id === classId);
          if (classData) {
            const studentCount = classData.countOfStudents 
              || classData.students_count 
              || classData.studentsCount 
              || classData.studentCount 
              || (classData.students && Array.isArray(classData.students) ? classData.students.length : 0)
              || 0;
            expectedStudents += studentCount;
          }
        });
      }
      
      // Check if we should wait for correct count during polling
      if (wasGenerating && expectedStudents > 0 && answerSheets.length !== expectedStudents) {
        console.log(`[ASSESSMENT PAGE] Waiting for correct count: ${answerSheets.length}/${expectedStudents}`);
        // Continue polling - don't set to ready yet
        return;
      }
      
      setAnswerSheetStatus('ready');
      setIsGenerating(false);
      
      // Show notification only if we were actively generating
      if (wasGenerating) {
        notificationController.success({
          message: 'Answer Sheets Ready!',
          description: `${answerSheets.length} answer sheet${answerSheets.length !== 1 ? 's' : ''} created successfully.`
        });
      }
    } else if (answerSheets.length === 0 && answerSheetStatus === 'ready') {
      // Reset to idle if answer sheets were deleted
      setAnswerSheetStatus('idle');
    }
  }, [answerSheets, answerSheetStatus, assessment, classes]);
  
  // Fetch answer sheets from API
  const fetchAnswerSheets = async () => {
    if (!assessmentId) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${URLs.BASE_URL}${URLs.GET_ANSWER_SHEETS_BY_ASSESSMENT_ENDPOINT}/${assessmentId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      const data = await response.json();
      console.log('[ASSESSMENT PAGE] Fetched answer sheets:', data);
      
      if (data.success && data.data?.answerSheets) {
        setAnswerSheets(data.data.answerSheets);
      } else if (data.success && Array.isArray(data.data)) {
        setAnswerSheets(data.data);
      } else {
        setAnswerSheets([]);
      }
    } catch (error) {
      console.error('[ASSESSMENT PAGE] Error fetching answer sheets:', error);
      setAnswerSheets([]);
    }
  };
  
  // Batch generate answer sheets
  const handleBatchGenerate = async () => {
    if (!assessmentId || !assessment) return;
    
    // Clear old answer sheets before regenerating
    setAnswerSheets([]);
    setIsGenerating(true);
    setAnswerSheetStatus('creating');
    
    try {
      const token = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('UserId');
      const response = await fetch(
        `${URLs.BASE_URL}${URLs.BATCH_GENERATE_ANSWER_SHEETS_ENDPOINT}/${assessmentId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId
          })
        }
      );
      
      const data = await response.json();
      console.log('[ASSESSMENT PAGE] Batch generation response:', data);
      
      if (response.ok && data.success) {
        notificationController.info({
          message: 'Creating Answer Sheets',
          description: 'Answer sheets are being generated. This may take a few moments...'
        });
        
        // Start polling immediately (no answer sheets in response yet)
        setAnswerSheetStatus('polling');
      } else {
        setAnswerSheetStatus('error');
        setIsGenerating(false);
        notificationController.error({
          message: 'Generation Failed',
          description: data.error || 'Failed to generate answer sheets. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('[ASSESSMENT PAGE] Error generating answer sheets:', error);
      setAnswerSheetStatus('error');
      setIsGenerating(false);
      notificationController.error({
        message: 'Generation Error',
        description: error.message || 'An error occurred while generating answer sheets.'
      });
    }
  };
  
  // Calculate statistics for publish modal
  const calculatePublishStats = () => {
    if (!assessment) {
      return { totalStudents: 0, studentsWithSheets: 0 };
    }
    
    // Priority 1: Use studentsCount directly from assessment data (if backend provides it)
    let totalStudents = assessment.studentsCount || assessment.countOfStudents || 0;
    
    // Priority 2: Calculate from classes if studentsCount not provided
    if (totalStudents === 0 && classes) {
      const assessmentClassIds = assessment.classIds || (assessment.classId ? [assessment.classId] : []);
      
      console.log('[ASSESSMENT PAGE] Calculating stats for classIds:', assessmentClassIds);
      console.log('[ASSESSMENT PAGE] Available classes:', classes);
      
      assessmentClassIds.forEach((classId: number) => {
        const classData = classes.find((c: any) => c.id === classId);
        if (classData) {
          // Try multiple possible field names for student count
          const studentCount = classData.countOfStudents 
            || classData.students_count 
            || classData.studentsCount 
            || classData.studentCount 
            || (classData.students && Array.isArray(classData.students) ? classData.students.length : 0)
            || 0;
          
          console.log(`[ASSESSMENT PAGE] Class ${classId} (${classData.name || classData.className}): ${studentCount} students`);
          totalStudents += studentCount;
        } else {
          console.warn(`[ASSESSMENT PAGE] Class ${classId} not found in loaded classes`);
        }
      });
      
      console.log('[ASSESSMENT PAGE] Total students calculated from classes:', totalStudents);
    } else {
      console.log('[ASSESSMENT PAGE] Using studentsCount from assessment data:', totalStudents);
    }
    
    // Calculate students with answer sheets
    // For now, assume all answer sheets cover all students
    // In real implementation, you'd match students to specific answer sheets
    const studentsWithSheets = answerSheets.length > 0 ? totalStudents : 0;
    
    return { totalStudents, studentsWithSheets };
  };
  
  // Handlers
  const handlePublish = () => {
    setPublishModalVisible(true);
  };
  
  const handlePublishConfirm = () => {
    if (!assessment) return;
    
    // Update assessment status to 'published'
    const updateModel = {
      status: 'published' as const
    };
    
    console.log('[PUBLISH] 📤 Sending update request with status:', updateModel.status);
    console.log('[PUBLISH] Assessment ID:', assessment.id);
    console.log('[PUBLISH] Update model:', updateModel);
    
    updateAssessmentRequest(assessment.id, updateModel);
    setPublishModalVisible(false);
    
    notificationController.success({
      message: 'Assessment Published!',
      description: `"${decodedAssessmentName}" has been published successfully.`
    });
    
    // Refresh assessment data with increased timeout
    setTimeout(() => {
      console.log('[PUBLISH] 🔄 Refreshing assessments list...');
      getAssessmentsRequest();
    }, 1500);
  };
  
  const handlePublishCancel = () => {
    setPublishModalVisible(false);
  };
  
  const handleDownloadAll = async () => {
    if (!assessmentId) return;
    
    try {
      notificationController.info({
        message: 'Downloading Answer Sheets',
        description: `Preparing ${answerSheets.length} answer sheet${answerSheets.length !== 1 ? 's' : ''} for download...`
      });
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${URLs.BASE_URL}${URLs.DOWNLOAD_ALL_ANSWER_SHEETS_ENDPOINT}/${assessmentId}/answer-sheets/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${decodedAssessmentName}-answer-sheets.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        notificationController.success({
          message: 'Download Started',
          description: 'Answer sheets are being downloaded.'
        });
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('[ASSESSMENT PAGE] Error downloading answer sheets:', error);
      notificationController.error({
        message: 'Download Failed',
        description: 'Failed to download answer sheets. Please try again.'
      });
    }
  };
  
  const handleCreateAnswerSheet = () => {
    // Check if students count is 0
    const stats = calculatePublishStats();
    if (stats.totalStudents === 0) {
      notificationController.warning({
        message: 'No Students',
        description: 'Cannot create answer sheets. No students are enrolled in the selected classes.'
      });
      return;
    }
    
    // Start batch generation
    handleBatchGenerate();
  };
  
  const publishStats = calculatePublishStats();
  
  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Get assessment statistics
  const assessmentStats = {
    status: assessment?.status || 'draft',
    questions: assessment?.questions || answerSheets[0]?.questions || 0,
    duration: assessment?.duration || 0,
    students: publishStats.totalStudents,
    date: assessment?.scheduledDate || ''
  };
  
  return (
    <>
      <PageTitle 
        title={`${decodedAssessmentName} - Assessment`} 
        description={`Manage answer sheets for ${decodedAssessmentName}`} 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        {/* Assessment Header */}
        <AssessmentHeader>
          <AssessmentTitle>
            <TitleRow>
              <span className="icon">📝</span>
              <h1>{decodedAssessmentName}</h1>
            </TitleRow>
            
            <StatsRow>
              {assessment?.type && (
                <StatItem>
                  <span className="stat-icon">📋</span>
                  <span className="stat-label">Type:</span>
                  <span className="stat-value">{assessment.type.charAt(0).toUpperCase() + assessment.type.slice(1)}</span>
                </StatItem>
              )}
              
              <StatItem>
                <span className="stat-icon">📊</span>
                <span className="stat-label">Status:</span>
                <StatusBadge status={assessmentStats.status}>
                  {assessmentStats.status}
                </StatusBadge>
              </StatItem>
              
              {assessmentStats.questions > 0 && (
                <StatItem>
                  <span className="stat-icon">❓</span>
                  <span className="stat-label">Questions:</span>
                  <span className="stat-value">{assessmentStats.questions}</span>
                </StatItem>
              )}
              
              {assessmentStats.duration > 0 && (
                <StatItem>
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-label">Duration:</span>
                  <span className="stat-value">{assessmentStats.duration} min</span>
                </StatItem>
              )}
              
              <StatItem>
                <span className="stat-icon">👥</span>
                <span className="stat-label">Students:</span>
                <span className="stat-value">{assessmentStats.students}</span>
              </StatItem>
              
              {assessmentStats.date && (
                <StatItem>
                  <span className="stat-icon">📅</span>
                  <span className="stat-label">Date:</span>
                  <span className="stat-value">{formatDate(assessmentStats.date)}</span>
                </StatItem>
              )}
            </StatsRow>
          </AssessmentTitle>
          
          <AssessmentActions>
            <PublishButton
              variant="primary"
              businessSize="medium"
              icon={<RocketOutlined />}
              onClick={handlePublish}
              disabled={answerSheetStatus !== 'ready' || assessment?.status === 'published'}
            >
              Publish
            </PublishButton>
          </AssessmentActions>
        </AssessmentHeader>
        
        {/* Answer Sheets Section */}
        <ContentCard>
          <AnswerSheetsInfoSection>
            {(answerSheetStatus === 'creating' || answerSheetStatus === 'polling') ? (
              <>
                <InfoIcon>⏳</InfoIcon>
                <InfoTitle>Creating Answer Sheets</InfoTitle>
                <InfoDescription>
                  Answer sheets are being generated for all students. 
                  This process may take a few moments...
                </InfoDescription>
                <CreatingButton text="Creating..." businessSize="large" />
              </>
            ) : answerSheetStatus === 'ready' && answerSheets.length > 0 ? (
              <>
                <InfoIcon>✅</InfoIcon>
                <InfoTitle>Answer Sheets Ready</InfoTitle>
                <SuccessBadge>
                  <span className="badge-icon">📄</span>
                  <span className="badge-text">
                    Answer sheets created: <span className="count">{answerSheets.length}</span>
                    {publishStats.totalStudents > 0 && ` / ${publishStats.totalStudents} students`}
                  </span>
                </SuccessBadge>
                
                {/* Warning if answer sheets count != students count */}
                {publishStats.totalStudents > 0 && answerSheets.length !== publishStats.totalStudents && (
                  <WarningBadge>
                    <span className="warning-icon">⚠️</span>
                    <span className="warning-text">
                      {answerSheets.length < publishStats.totalStudents ? (
                        <>
                          Only <span className="highlight">{answerSheets.length}</span> answer sheets for{' '}
                          <span className="highlight">{publishStats.totalStudents}</span> students. 
                          Some students may not have answer sheets.
                        </>
                      ) : (
                        <>
                          <span className="highlight">{answerSheets.length}</span> answer sheets for{' '}
                          <span className="highlight">{publishStats.totalStudents}</span> students. 
                          Number of students may have changed.
                        </>
                      )}
                      {' '}Consider regenerating to match current student count.
                    </span>
                  </WarningBadge>
                )}
                
                <InfoDescription>
                  {answerSheets.length === publishStats.totalStudents || publishStats.totalStudents === 0
                    ? 'All answer sheets for this assessment are ready. You can download them below.'
                    : 'You can download existing answer sheets or regenerate to match current student count.'}
                </InfoDescription>
                
                <ButtonGroup>
                  <DownloadButton
                    variant="primary"
                    businessSize="large"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadAll}
                  >
                    Download All Answer Sheets
                  </DownloadButton>
                  
                  {/* Show Regenerate button if sheets count != students count */}
                  {publishStats.totalStudents > 0 && answerSheets.length !== publishStats.totalStudents && (
                    <BusinessButton
                      variant="warning"
                      businessSize="large"
                      icon={<PlusOutlined />}
                      onClick={handleCreateAnswerSheet}
                    >
                      Regenerate Answer Sheets
                    </BusinessButton>
                  )}
                </ButtonGroup>
              </>
            ) : (
              <>
                <InfoIcon>📋</InfoIcon>
                <InfoTitle>No Answer Sheets Created</InfoTitle>
                <InfoDescription>
                  Answer sheets have not been created for this assessment yet. 
                  Students will need answer sheets to take this exam.
                </InfoDescription>
                <CreateButton
                  variant="primary"
                  businessSize="large"
                  icon={<PlusOutlined />}
                  onClick={handleCreateAnswerSheet}
                  disabled={publishStats.totalStudents === 0}
                >
                  Create Answer Sheet
                </CreateButton>
              </>
            )}
          </AnswerSheetsInfoSection>
        </ContentCard>
        
        {/* Publish Confirmation Modal */}
        <PublishAssessmentModal
          isOpen={publishModalVisible}
          onClose={handlePublishCancel}
          onConfirm={handlePublishConfirm}
          assessmentName={decodedAssessmentName}
          totalStudents={publishStats.totalStudents}
          studentsWithSheets={publishStats.studentsWithSheets}
          isLoading={loading}
        />
      </PageContainer>
    </>
  );
};

export default AssessmentPage;

